import React from "react";
import { SearchIcon } from "../icons";
import roddoFish from "../../assets/roddo-fish.png";
import BigBlueQuickWindow from "../BigBlueQuickWindow";
import CrewDMConversationMenu from "../CrewDMConversationMenu";
import CrewDMView from "../CrewDMView";
import { FishSpeciesRow, getAreaActivityBadgeLabel, getFishAreaEmoji } from "./FishIQMapPresentation";

type Props = { ctx: any };
const BigBlueQuickOverlays: React.FC<Props> = ({ ctx }) => {
  const {
    closeBigBlueQuickWindow,
    crewPresence,
    findCatchMemoryMatch,
    fishWidgetAnchor,
    inferGeoProfile,
    isDarkMode,
    latestUserLatLngRef,
    lifetimeCatchSpeciesRows,
    mapInstanceRef,
    nearbyCatchSpeciesRows,
    profile,
    props,
    quickChallengeCompletions,
    quickChallengeRows,
    quickCrewDmCard,
    quickCrewDmInboxByUid,
    quickCrewMode,
    quickCrewPresenceOptIn,
    quickCrewPresenceSaving,
    quickCrewRelationshipByTarget,
    quickCrewSearch,
    quickCrewSearchBusyUid,
    quickCrewSearchResults,
    quickCrewSendRequest,
    quickCrewSummary,
    quickCrewUnreadCount,
    quickDailyPreview,
    quickDailyRows,
    quickFishIQSearch,
    quickGroupCrews,
    quickWindow,
    setFishWidgetAreaSource,
    setFishWidgetMode,
    setIsFishWidgetOpen,
    setQuickCrewDmUid,
    setQuickCrewMode,
    setQuickCrewSearch,
    setQuickFishIQSearch,
    setQuickWindow,
    setSelectedFishSpot,
    toggleQuickCrewPresence,
    visibleFishSpots
  } = ctx;
  return (<>
      {props.isActive && quickWindow === "daily" ? (
        <BigBlueQuickWindow
          title="Today’s Bite Plan"
          subtitle={`${quickDailyRows.filter((row) => row.done && !row.optional).length}/3 core tasks complete`}
          icon={<span aria-hidden="true">🧭</span>}
          isDarkMode={isDarkMode}
          accent="gold"
          anchorId="daily"
          placement="right"
          anchorOffsetY={18}
          onClose={closeBigBlueQuickWindow}
          onOpenFull={() => {
            setQuickWindow(null);
            props.onBitePlanClick?.();
          }}
          openFullLabel="Open Bite Plan"
        >
          <div className="space-y-3">
            <div
              className="rounded-[15px] border p-3.5"
              style={{
                background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.68)",
                borderColor: isDarkMode ? "rgba(132,151,190,0.12)" : "rgba(91,67,49,0.09)",
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={{ color: isDarkMode ? "#F2B84B" : "#A66A0B" }}>Today’s Cast</div>
                  <div className="mt-1 text-[13px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>{quickDailyPreview.dailyTask?.title || "Complete today’s Roddo task"}</div>
                  <div className="mt-1 text-[10.5px] font-semibold leading-4" style={{ color: isDarkMode ? "#97A6BA" : "#756A60" }}>{quickDailyPreview.dailyTask?.description || "Keep your fishing loop moving."}</div>
                </div>
                <div className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black" style={{ background: isDarkMode ? "rgba(240,200,109,0.10)" : "rgba(201,149,47,0.10)", color: isDarkMode ? "#F0C86D" : "#A87518" }}>+{quickDailyPreview.dailyTask?.rewardShells || 0} 🐚</div>
              </div>
            </div>

            {quickDailyRows.map((row) => (
              <div
                key={row.key}
                className="flex min-h-[58px] items-center gap-3 rounded-[15px] border px-3 py-2.5"
                style={{
                  background: row.done
                    ? isDarkMode ? "rgba(98,215,163,0.08)" : "rgba(61,187,134,0.08)"
                    : isDarkMode ? "#0F1828" : "rgba(255,255,255,0.62)",
                  borderColor: row.done
                    ? isDarkMode ? "rgba(98,215,163,0.17)" : "rgba(61,187,134,0.16)"
                    : isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)",
                }}
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[11px] text-[18px]" style={{ background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(248,243,236,0.92)" }}>{row.icon}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-black" style={{ color: isDarkMode ? "#EDF2FA" : "#34424C" }}>{row.title}</div>
                  <div className="mt-0.5 text-[9.5px] font-bold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>{row.optional ? "Optional" : "Core task"}</div>
                </div>
                <div className="text-[16px] font-black" style={{ color: row.done ? (isDarkMode ? "#62D7A3" : "#2F9E68") : (isDarkMode ? "#6F7E93" : "#A89B8D") }}>{row.done ? "✓" : "○"}</div>
              </div>
            ))}
          </div>
        </BigBlueQuickWindow>
      ) : null}

      {props.isActive && quickWindow === "crew" ? (
        <BigBlueQuickWindow
          title={
            quickCrewMode === "find"
              ? "Find an Angler"
              : quickCrewMode === "dm"
                ? quickCrewDmCard?.displayName || "Crew Message"
                : "Crew Dock"
          }
          subtitle={
            quickCrewMode === "find"
              ? "Search by name or username"
              : quickCrewMode === "dm"
                ? quickCrewDmCard?.isOnline
                  ? "Online · private Crew message"
                  : "Private Crew message"
                : `${quickCrewSummary.accepted.length} crewmate${quickCrewSummary.accepted.length === 1 ? "" : "s"} · ${crewPresence.length} online${quickCrewUnreadCount ? ` · ${quickCrewUnreadCount} unread` : ""}`
          }
          icon={
            quickCrewMode === "dm" && quickCrewDmCard?.avatarUrl ? (
              <div className="relative h-full w-full">
                <img
                  src={quickCrewDmCard.avatarUrl}
                  alt=""
                  className="h-full w-full rounded-[10px] object-cover"
                />
                <span
                  className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2"
                  style={{
                    background: quickCrewDmCard.isOnline ? "#34D399" : (isDarkMode ? "#625C72" : "#A8A29E"),
                    borderColor: isDarkMode ? "#0A1221" : "#FBF8F2",
                  }}
                  aria-hidden="true"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  void toggleQuickCrewPresence();
                }}
                disabled={quickCrewPresenceSaving || !String((profile as any)?.uid || "").trim()}
                className="relative grid h-full w-full place-items-center rounded-[10px] disabled:opacity-60"
                aria-label={quickCrewPresenceOptIn ? "Hide crew map presence" : "Show crew map presence"}
                title={quickCrewPresenceOptIn ? "Visible to crew" : "Hidden from crew"}
              >
                <span
                  className="text-[23px] leading-none"
                  aria-hidden="true"
                  style={{
                    filter: quickCrewPresenceOptIn ? "none" : "grayscale(0.12)",
                    opacity: quickCrewPresenceOptIn ? 1 : 0.82,
                  }}
                >
                  {quickCrewPresenceOptIn ? "🐵" : "🙈"}
                </span>
                <span
                  className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2"
                  aria-hidden="true"
                  style={{
                    background: quickCrewPresenceOptIn ? "#34D399" : "#73717f",
                    borderColor: isDarkMode ? "#0A1221" : "#FBF8F2",
                  }}
                />
              </button>
            )
          }
          isDarkMode={isDarkMode}
          accent="violet"
          anchorId="crew"
          placement="left"
          anchorOffsetY={28}
          headerAction={
            quickCrewMode === "find" ? (
              <button
                type="button"
                onClick={() => {
                  setQuickCrewMode("summary");
                  setQuickCrewSearch("");
                  setQuickCrewDmUid("");
                }}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[19px] font-black"
                style={{
                  background: isDarkMode ? "#101A2A" : "#FFFFFF",
                  borderColor: isDarkMode ? "rgba(132,151,190,0.13)" : "rgba(91,67,49,0.09)",
                  color: isDarkMode ? "#F0C86D" : "#2F80ED",
                }}
                aria-label="Back to Crew Dock"
                title="Back"
              >
                ‹
              </button>
            ) : quickCrewMode === "dm" && quickCrewDmCard ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setQuickCrewMode("summary");
                    setQuickCrewSearch("");
                    setQuickCrewDmUid("");
                  }}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[19px] font-black"
                  style={{
                    background: isDarkMode ? "#101A2A" : "#FFFFFF",
                    borderColor: isDarkMode ? "rgba(132,151,190,0.13)" : "rgba(91,67,49,0.09)",
                    color: isDarkMode ? "#F0C86D" : "#2F80ED",
                  }}
                  aria-label="Back to Crew Dock"
                  title="Back"
                >
                  ‹
                </button>

                <CrewDMConversationMenu
                  dmId={String(quickCrewDmCard.rel?.id || quickCrewDmInboxByUid.get(quickCrewDmCard.uid)?.id || "")}
                  currentUid={String(profile?.uid || "")}
                  targetUid={quickCrewDmCard.uid}
                  targetName={quickCrewDmCard.displayName}
                  isDarkMode={isDarkMode}
                  addToast={(message, type) => props.addToast?.(message, type)}
                  onDeleted={() => {
                    setQuickCrewDmUid("");
                    setQuickCrewMode("summary");
                  }}
                  onBlocked={() => {
                    setQuickCrewDmUid("");
                    setQuickCrewMode("summary");
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setQuickCrewMode("find")}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[21px] font-black"
                style={{
                  background: isDarkMode ? "rgba(240,200,109,0.12)" : "rgba(47,128,237,0.10)",
                  borderColor: isDarkMode ? "rgba(240,200,109,0.22)" : "rgba(47,128,237,0.20)",
                  color: isDarkMode ? "#F0C86D" : "#2F80ED",
                }}
                aria-label="Find or add angler"
                title="Find angler"
              >
                +
              </button>
            )
          }
          onClose={closeBigBlueQuickWindow}
          onOpenFull={
            quickCrewMode === "dm"
              ? undefined
              : () => {
                  setQuickCrewMode("summary");
                  setQuickCrewSearch("");
                  setQuickCrewDmUid("");
                  setQuickWindow(null);
                  props.onCrewClick?.();
                }
          }
          openFullLabel="Open Crew Dock" 
        >
          {quickCrewMode === "find" ? (
            <div className="space-y-3">
              <div className="relative">
                <input
                  autoFocus
                  value={quickCrewSearch}
                  onChange={(event) => setQuickCrewSearch(event.target.value)}
                  placeholder="Search name or username..."
                  className="w-full rounded-[14px] border px-3.5 py-3 pr-10 text-[12px] font-bold outline-none"
                  style={{
                    background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.72)",
                    borderColor: isDarkMode ? "rgba(132,151,190,0.12)" : "rgba(91,67,49,0.10)",
                    color: isDarkMode ? "#F2F6FC" : "#34424C",
                  }}
                />
                <SearchIcon
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2"
                  style={{ color: isDarkMode ? "#AFA3F7" : "#6551B9" }}
                />
              </div>

              {String(quickCrewSearch || "").trim().length < 2 ? (
                <div className="rounded-[15px] border px-4 py-5 text-center" style={{ background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.62)", borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)" }}>
                  <div className="text-[20px]">🔎</div>
                  <div className="mt-2 text-[12px] font-black" style={{ color: isDarkMode ? "#EDF2FA" : "#34424C" }}>Find a trusted angler</div>
                  <div className="mt-1 text-[10px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>Type at least 2 characters to search.</div>
                </div>
              ) : quickCrewSearchResults.length ? (
                <div className="space-y-2">
                  {quickCrewSearchResults.map((row) => {
                    const rel = quickCrewRelationshipByTarget.get(row.targetUid);
                    const accepted = String(rel?.status || "") === "accepted";
                    const pending = String(rel?.status || "") === "pending";
                    const incoming = pending && String(rel?.recipientId || "") === String(profile?.uid || "");
                    const busy = quickCrewSearchBusyUid === row.targetUid;
                    const label = accepted ? "Crew" : pending ? (incoming ? "Incoming" : "Sent") : "Add";
                    const disabled = accepted || pending || busy;

                    return (
                      <div
                        key={row.targetUid}
                        className="flex items-center gap-3 rounded-[15px] border px-3 py-2.5"
                        style={{
                          background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.66)",
                          borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)",
                        }}
                      >
                        <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full border" style={{ borderColor: isDarkMode ? "rgba(132,151,190,0.16)" : "rgba(91,67,49,0.10)" }}>
                          {row.avatarUrl ? <img src={row.avatarUrl} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] font-black">{row.displayName.split(/\s+/).slice(0, 2).map((part) => part[0] || "").join("").toUpperCase() || "A"}</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[12px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#34424C" }}>{row.displayName}</div>
                          <div className="mt-0.5 truncate text-[9.5px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>{row.username ? `@${row.username.replace(/^@/, "")}` : "Roddo angler"}</div>
                        </div>
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => void quickCrewSendRequest(row.targetUid, row.displayName)}
                          className="rounded-[10px] border px-3 py-2 text-[10.5px] font-black disabled:opacity-60"
                          style={{
                            background: accepted || pending
                              ? (isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(245,242,237,0.9)")
                              : (isDarkMode ? "rgba(240,200,109,0.14)" : "rgba(47,128,237,0.10)"),
                            borderColor: isDarkMode ? "rgba(132,151,190,0.12)" : "rgba(91,67,49,0.10)",
                            color: accepted || pending
                              ? (isDarkMode ? "#95A2B4" : "#80756B")
                              : (isDarkMode ? "#F0C86D" : "#2F80ED"),
                          }}
                        >
                          {busy ? "..." : label}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-[15px] border px-4 py-5 text-center" style={{ background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.62)", borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)" }}>
                  <div className="text-[12px] font-black" style={{ color: isDarkMode ? "#EDF2FA" : "#34424C" }}>No anglers found</div>
                  <div className="mt-1 text-[10px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>Try another name or username.</div>
                </div>
              )}
            </div>
          ) : quickCrewMode === "dm" && quickCrewDmCard ? (
            <CrewDMView
              compact
              dmId={String(quickCrewDmCard.rel?.id || quickCrewDmInboxByUid.get(quickCrewDmCard.uid)?.id || "")}
              targetUid={quickCrewDmCard.uid}
              targetName={quickCrewDmCard.displayName}
              targetAvatar={quickCrewDmCard.avatarUrl}
              targetOnline={quickCrewDmCard.isOnline}
              currentUser={profile as UserProfile}
              isDarkMode={isDarkMode}
              addToast={(message, type) => props.addToast?.(message, type)}
              onBack={() => {
                setQuickCrewDmUid("");
                setQuickCrewMode("summary");
              }}
            />
          ) : (
            <div className="space-y-3">
              {quickCrewSummary.crewmates.length ? (
                <div>
                  <div className="mb-2.5 flex items-center justify-between gap-3">
                    <div className="text-[8.5px] font-black uppercase tracking-[0.15em]" style={{ color: isDarkMode ? "#AFA3F7" : "#6551B9" }}>Crewmates</div>
                    <div className="text-[9px] font-black" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>
                      {quickCrewSummary.crewmates.filter((card) => card.isOnline).length} online
                      {quickCrewUnreadCount ? ` · ${quickCrewUnreadCount} unread` : ""}
                    </div>
                  </div>
                  <div className="-mx-1 overflow-x-auto px-1 pb-1 pt-2 pr-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <div className="flex min-w-max items-center gap-2.5">
                      {quickCrewSummary.crewmates.slice(0, 12).map((card) => {
                        const inbox = quickCrewDmInboxByUid.get(card.uid);
                        return (
                          <button
                            key={card.uid || card.rel?.id}
                            type="button"
                            onClick={() => {
                              setQuickCrewSearch("");
                              setQuickCrewDmUid(card.uid);
                              setQuickCrewMode("dm");
                            }}
                            className="relative h-12 w-12 shrink-0 overflow-visible rounded-full transition active:scale-[0.96]"
                            title={`${card.displayName} · ${card.isOnline ? "Online" : "Offline"}${inbox?.unread ? ` · ${Math.max(1, Number(inbox.unreadCount || 1))} unread` : ""}`}
                            aria-label={`Message ${card.displayName}, ${card.isOnline ? "online" : "offline"}${inbox?.unread ? `, ${Math.max(1, Number(inbox.unreadCount || 1))} unread message${Math.max(1, Number(inbox.unreadCount || 1)) === 1 ? "" : "s"}` : ""}`}
                          >
                            <div
                              className="h-12 w-12 overflow-hidden rounded-full border"
                              style={{
                                background: isDarkMode ? "#101827" : "rgba(255,255,255,0.78)",
                                borderColor: inbox?.unread
                                  ? (isDarkMode ? "rgba(142,167,255,0.70)" : "rgba(47,128,237,0.62)")
                                  : card.isOnline
                                    ? (isDarkMode ? "rgba(98,215,163,0.34)" : "rgba(61,187,134,0.34)")
                                    : (isDarkMode ? "rgba(132,151,190,0.16)" : "rgba(91,67,49,0.10)"),
                                boxShadow: inbox?.unread
                                  ? (isDarkMode ? "0 0 0 2px rgba(142,167,255,0.12)" : "0 0 0 2px rgba(47,128,237,0.10)")
                                  : card.isOnline
                                    ? (isDarkMode ? "0 0 0 2px rgba(52,211,153,0.07)" : "0 0 0 2px rgba(47,158,104,0.06)")
                                    : "none",
                              }}
                            >
                              {card.avatarUrl ? (
                                <img src={card.avatarUrl} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="grid h-full w-full place-items-center text-[11px] font-black" style={{ color: isDarkMode ? "#DDE5F0" : "#5D544B" }}>
                                  {card.displayName.split(/\s+/).slice(0, 2).map((part) => part[0] || "").join("").toUpperCase() || "FC"}
                                </div>
                              )}
                            </div>

                            <span
                              className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2"
                              style={{
                                background: card.isOnline ? "#34D399" : (isDarkMode ? "#625C72" : "#A8A29E"),
                                borderColor: isDarkMode ? "#0A1120" : "#FFFCF7",
                              }}
                              aria-hidden="true"
                            />

                            {inbox?.unread ? (
                              <span
                                className="pointer-events-none absolute right-0 top-0 z-20 grid h-[17px] min-w-[17px] place-items-center rounded-full border-2 px-[3px] text-[8px] font-black leading-none text-white shadow-[0_2px_5px_rgba(0,0,0,0.22)]"
                                style={{
                                  background: isDarkMode ? "#8EA7FF" : "#2F80ED",
                                  borderColor: isDarkMode ? "#0A1120" : "#FFFCF7",
                                }}
                                aria-hidden="true"
                              >
                                {Number(inbox.unreadCount || 1) > 99
                                  ? "99+"
                                  : Math.max(1, Number(inbox.unreadCount || 1))}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-[15px] border px-4 py-5 text-center" style={{ background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.62)", borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)" }}>
                  <div className="text-[21px]">👥</div>
                  <div className="mt-2 text-[12px] font-black" style={{ color: isDarkMode ? "#EDF2FA" : "#34424C" }}>No crewmates yet</div>
                  <div className="mt-1 text-[10px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>Tap + above to find trusted anglers.</div>
                </div>
              )}

              {quickCrewSummary.pending.length ? <div className="rounded-[15px] border px-3.5 py-2.5 text-[10.5px] font-black" style={{ background: isDarkMode ? "rgba(242,184,75,0.08)" : "rgba(242,184,75,0.10)", borderColor: isDarkMode ? "rgba(242,184,75,0.16)" : "rgba(185,128,18,0.14)", color: isDarkMode ? "#F2D58E" : "#8B610C" }}>{quickCrewSummary.pending.length} crew request{quickCrewSummary.pending.length === 1 ? "" : "s"} waiting</div> : null}

              <div>
                <div className="mb-2.5 text-[8.5px] font-black uppercase tracking-[0.15em]" style={{ color: isDarkMode ? "#AFA3F7" : "#6551B9" }}>My crews</div>
                <div className="space-y-2">
                  {quickGroupCrews.length ? quickGroupCrews.slice(0, 8).map((crew) => (
                    <button
                      key={crew.id}
                      type="button"
                      onClick={() => {
                        setQuickWindow(null);
                        props.onCrewClick?.({ crewId: crew.id, from: "map" });
                      }}
                      className="group flex min-h-[58px] w-full items-center gap-3 rounded-[15px] border px-3 py-2.5 text-left transition duration-150 hover:-translate-y-px active:translate-y-0 active:scale-[0.99]"
                      style={{
                        background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.64)",
                        borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)",
                      }}
                      aria-label={`Open ${crew.title || "Fishing Crew"}`}
                      title={`Open ${crew.title || "Fishing Crew"}`}
                    >
                      <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-[11px] text-[17px]" style={{ background: isDarkMode ? "rgba(138,112,242,0.10)" : "rgba(47,128,237,0.08)" }}>{crew.coverThumbUrl || crew.coverImageUrl ? <img src={crew.coverThumbUrl || crew.coverImageUrl} alt="" className="h-full w-full object-cover" /> : "🎣"}</div>
                      <div className="min-w-0 flex-1"><div className="truncate text-[12px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#34424C" }}>{crew.title || "Fishing Crew"}</div><div className="mt-0.5 text-[9.5px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>{Math.max(1, crew.memberIds?.length || 1)} members</div></div>
                      <span aria-hidden="true" className="shrink-0 text-[16px] font-black opacity-45 transition group-hover:translate-x-0.5 group-hover:opacity-70">›</span>
                    </button>
                  )) : <div className="rounded-[15px] border px-3.5 py-3.5 text-[10.5px] font-semibold leading-4" style={{ borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)", color: isDarkMode ? "#97A6BA" : "#756A60" }}>No group crews yet. Open Crew Dock to create one or manage private crew boards.</div>}
                </div>
              </div>
            </div>
          )}
        </BigBlueQuickWindow>
      ) : null}
      {props.isActive && quickWindow === "challenges" ? (
        <BigBlueQuickWindow
          title="Challenges"
          subtitle={`${quickChallengeRows.filter((row) => !row.completed).length} shown active · ${Object.keys(quickChallengeCompletions || {}).length} completed`}
          icon={<span aria-hidden="true">🚩</span>}
          isDarkMode={isDarkMode}
          accent="green"
          anchorId="challenges"
          placement="up-left"
          onClose={closeBigBlueQuickWindow}
          onOpenFull={() => {
            setQuickWindow(null);
            props.onChallengesClick?.();
          }}
          openFullLabel="Open Challenges"
        >
          <div className="space-y-3">
            {quickChallengeRows.map(({ challenge, current, total, percent, completed }) => (
              <div key={challenge.id} className="rounded-[15px] border p-3.5" style={{ background: completed ? (isDarkMode ? "rgba(98,215,163,0.07)" : "rgba(61,187,134,0.07)") : (isDarkMode ? "#0F1828" : "rgba(255,255,255,0.64)"), borderColor: completed ? (isDarkMode ? "rgba(98,215,163,0.15)" : "rgba(61,187,134,0.14)") : (isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)") }}>
                <div className="flex items-start gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[11px] text-[17px]" style={{ background: isDarkMode ? "rgba(156,247,191,0.08)" : "rgba(47,158,88,0.08)" }}>{completed ? "✓" : "🚩"}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="truncate text-[12px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#34424C" }}>{challenge.title}</div>
                      <div className="shrink-0 text-[9.5px] font-black" style={{ color: isDarkMode ? "#F2C86B" : "#A87518" }}>+{challenge.rewardXP} XP</div>
                    </div>
                    <div className="mt-1 text-[10px] font-semibold leading-4" style={{ color: isDarkMode ? "#97A6BA" : "#756A60" }}>{challenge.description}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full" style={{ background: isDarkMode ? "rgba(255,255,255,0.07)" : "rgba(91,67,49,0.08)" }}>
                        <div className="h-full rounded-full" style={{ width: `${percent}%`, background: completed ? "#62D7A3" : (isDarkMode ? "#9CF7BF" : "#2F9E58") }} />
                      </div>
                      <div className="shrink-0 text-[9px] font-black" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>{Math.min(current, total)}/{total}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </BigBlueQuickWindow>
      ) : null}

      {props.isActive && quickWindow === "fishiq" ? (
        <BigBlueQuickWindow
          title="Water Read"
          subtitle={fishWidgetAnchor ? "Current Big Blue read" : "Fish intelligence around your map"}
          icon={<img src={roddoFish} alt="" className="h-7 w-8 object-contain" />}
          isDarkMode={isDarkMode}
          accent="aqua"
          anchorId="fishiq"
          placement="up-right"
          onClose={closeBigBlueQuickWindow}
          onOpenFull={() => {
            const liveMapCenter = mapInstanceRef.current?.getCenter?.();
            const anchorForFishIQ = fishWidgetAnchor ?? latestUserLatLngRef.current ?? (liveMapCenter ? { lat: liveMapCenter.lat, lng: liveMapCenter.lng } : null);
            const geo = anchorForFishIQ ? inferGeoProfile(anchorForFishIQ, props.locationName ?? null) : null;
            setQuickFishIQSearch("");
            setQuickWindow(null);
            props.onFishIQClick?.({
              mapContext: geo && anchorForFishIQ ? {
                source: "map button",
                coords: { lat: anchorForFishIQ.lat, lon: anchorForFishIQ.lng },
                waterType: geo.waterKind === "saltwater" ? "inshore" : geo.waterKind,
                waterKind: geo.waterKind === "saltwater" ? "inshore" : geo.waterKind,
                waterName: props.locationName || (geo.waterKind === "saltwater" ? "Coastal water" : geo.waterKind === "river" ? "Moving water" : geo.waterKind === "lake" ? "Lake / reservoir water" : "Freshwater profile"),
                regionHint: geo.region,
              } : null,
            });
          }}
          openFullLabel="Open Water Read"
        >
          {(() => {
            const normalizedQuickFishIQSearch = quickFishIQSearch.trim().toLowerCase();
            const filteredFishSpots = visibleFishSpots.filter((spot) => {
              if (!normalizedQuickFishIQSearch) return true;
              const speciesData = (spot as any)?.fishIQSpeciesData || {};
              const speciesLabel = String((spot as any)?.species || (spot as any)?.name || speciesData?.commonName || "Fish");
              const scientificName = String(speciesData?.scientificName || speciesData?.taxonomy?.scientificName || "");
              const haystack = `${speciesLabel} ${scientificName}`.toLowerCase();
              return haystack.includes(normalizedQuickFishIQSearch);
            });

            return (
              <div className="space-y-3">
                <div
                  className="rounded-[15px] border px-3 py-2.5"
                  style={{
                    background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.74)",
                    borderColor: isDarkMode ? "rgba(132,151,190,0.12)" : "rgba(91,67,49,0.09)",
                  }}
                >
                  <div className="relative">
                    <input
                      type="text"
                      value={quickFishIQSearch}
                      onChange={(event) => setQuickFishIQSearch(event.target.value)}
                      placeholder="Search fish in this area..."
                      className="w-full rounded-[13px] border px-3 py-[10px] pr-9 text-[12px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/20 dark:focus:ring-[#F0C86D]/25"
                      style={{
                        background: isDarkMode ? "rgba(8,14,26,0.82)" : "rgba(248,244,238,0.96)",
                        borderColor: isDarkMode ? "rgba(132,151,190,0.12)" : "rgba(91,67,49,0.08)",
                        color: isDarkMode ? "#F2F6FC" : "#314150",
                      }}
                    />
                    <div
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: isDarkMode ? "#F0C86D" : "#2F80ED" }}
                    >
                      <SearchIcon className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                {visibleFishSpots.length ? (
                  filteredFishSpots.length ? (
                    filteredFishSpots.map((spot, index) => {
                      const speciesData = (spot as any)?.fishIQSpeciesData || {};
                      const speciesLabel = String((spot as any)?.species || (spot as any)?.name || speciesData?.commonName || "Fish");
                      const localMemory = findCatchMemoryMatch(nearbyCatchSpeciesRows, speciesLabel, (spot as any)?.id);
                      const lifetimeMemory = localMemory
                        ? null
                        : findCatchMemoryMatch(lifetimeCatchSpeciesRows, speciesLabel, (spot as any)?.id);
                      const memory = localMemory || lifetimeMemory;
                      const catchLabel = localMemory
                        ? (localMemory.count > 1 ? `✓ · ${localMemory.count}` : "✓")
                        : lifetimeMemory
                          ? "✓"
                          : "?";
                      return (
                        <FishSpeciesRow
                          key={`${String((spot as any)?.id || speciesLabel)}:${index}`}
                          isDarkMode={isDarkMode}
                          speciesLabel={speciesLabel}
                          speciesData={speciesData}
                          waterLabel={String((spot as any)?.waterName || props.locationName || "")}
                          primaryBadgeLabel={getAreaActivityBadgeLabel(index)}
                          catchCountLabel={catchLabel}
                          summaryLabel={String((speciesData as any)?.summary || "")}
                          canOpenDetail={true}
                          showChevron={false}
                          icon={getFishAreaEmoji(speciesLabel, String((spot as any)?.waterName || ""))}
                          onClick={() => {
                            setQuickWindow(null);
                            setQuickFishIQSearch("");
                            setSelectedFishSpot({ ...(spot as any), __roddoCatches: memory?.catches || [] } as any);
                            setFishWidgetAreaSource("viewport");
                            setFishWidgetMode("area");
                            setIsFishWidgetOpen(true);
                          }}
                        />
                      );
                    })
                  ) : (
                    <div className="rounded-[15px] border px-4 py-5 text-center" style={{ background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.62)", borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)" }}>
                      <img src={roddoFish} alt="" className="mx-auto h-9 w-10 object-contain opacity-80" />
                      <div className="mt-2 text-[12px] font-black" style={{ color: isDarkMode ? "#EDF2FA" : "#34424C" }}>No fish matches that search yet</div>
                      <div className="mt-1 text-[10px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>Try another species name or clear the search to see the full local list.</div>
                    </div>
                  )
                ) : (
                  <div className="rounded-[15px] border px-4 py-5 text-center" style={{ background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.62)", borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)" }}>
                    <img src={roddoFish} alt="" className="mx-auto h-9 w-10 object-contain opacity-80" />
                    <div className="mt-2 text-[12px] font-black" style={{ color: isDarkMode ? "#EDF2FA" : "#34424C" }}>No water read loaded here yet</div>
                    <div className="mt-1 text-[10px] font-semibold" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>Double-click or use Search this area, then reopen Water Read for the local species list.</div>
                  </div>
                )}
              </div>
            );
          })()}
        </BigBlueQuickWindow>
      ) : null}

  </>);
};
export default BigBlueQuickOverlays;
